/*
 * Yotpo store (site) reviews carousel.
 *
 * Data: Yotpo public storefront endpoint for site reviews only
 *   https://api-cdn.yotpo.com/v1/widget/{app_key}/products/yotpo_site_reviews/reviews.json
 * Only the public app key is used. Review text is untrusted and is only ever
 * written with textContent. Customer emails and user IDs are never kept.
 */
(() => {
  if (customElements.get('yotpo-store-reviews')) return;

  const ENDPOINT = 'https://api-cdn.yotpo.com/v1/widget/';
  const SITE_DOMAIN_KEY = 'yotpo_site_reviews';
  const APP_KEY_PATTERN = /^[A-Za-z0-9_-]{10,64}$/;
  const PER_PAGE = 50; // Yotpo recommends 50 reviews per request
  const MAX_PAGES = 4;
  const REQUEST_TIMEOUT_MS = 8000;

  const CACHE_PREFIX = 'yotpoStoreReviews:v1:';
  const CACHE_TTL_MS = 30 * 60 * 1000;
  const CACHE_MAX_TTL_MS = 60 * 60 * 1000;
  const COOLDOWN_MS = 5 * 60 * 1000;

  const dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Dublin',
  });

  // One request per app key + query, shared by every widget instance on the page.
  const inflight = new Map();

  class ReviewsError extends Error {
    constructor(code, message) {
      super(message);
      this.code = code;
    }
  }

  const storage = {
    get(key) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        // Storage full, blocked or unavailable: carry on without caching.
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {}
    },
  };

  function readFresh(key) {
    const entry = storage.get(key);
    if (!entry || typeof entry.expires !== 'number') return null;
    const remaining = entry.expires - Date.now();
    if (remaining <= 0 || remaining > CACHE_MAX_TTL_MS) {
      storage.remove(key);
      return null;
    }
    return entry;
  }

  // Decode any HTML entities / strip markup without executing anything:
  // DOMParser documents are inert (no scripts run, no resources load).
  function cleanText(value, maxLength) {
    if (typeof value !== 'string') return '';
    let text = value;
    if (/[&<]/.test(text)) {
      try {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        doc.querySelectorAll('script, style, template, noscript').forEach((node) => node.remove());
        text = doc.body ? doc.body.textContent || '' : '';
      } catch (error) {
        text = '';
      }
    }
    text = text.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
  }

  async function fetchPage(appKey, page) {
    const url = `${ENDPOINT}${encodeURIComponent(appKey)}/products/${SITE_DOMAIN_KEY}/reviews.json?per_page=${PER_PAGE}&page=${page}`;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(url, { credentials: 'omit', signal: controller.signal });
    } catch (error) {
      throw new ReviewsError(
        error.name === 'AbortError' ? 'timeout' : 'network',
        error.name === 'AbortError' ? 'Yotpo did not respond in time.' : 'Could not reach Yotpo (network or CORS error).'
      );
    } finally {
      window.clearTimeout(timer);
    }

    if (response.status === 429) throw new ReviewsError('rate_limited', 'Yotpo rate limit reached (HTTP 429).');
    if (response.status === 404) throw new ReviewsError('not_found', 'Yotpo returned HTTP 404. Check the app key.');
    if (!response.ok) throw new ReviewsError('http', `Yotpo returned HTTP ${response.status}.`);

    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new ReviewsError('invalid_json', 'Yotpo response was not valid JSON.');
    }

    const body = data && data.response;
    if (!body || typeof body !== 'object' || !Array.isArray(body.reviews)) {
      throw new ReviewsError('malformed', 'Yotpo response is missing the reviews list.');
    }
    return body;
  }

  // Returns the Yotpo product id of the site-reviews "product", or null if not supplied.
  function getSiteProductId(body) {
    if (!Array.isArray(body.products) || body.products.length === 0) return null;
    const site = body.products.find((product) => product && product.domain_key === SITE_DOMAIN_KEY);
    if (!site) throw new ReviewsError('wrong_source', 'Response is not for yotpo_site_reviews.');
    return site.id != null ? String(site.id) : null;
  }

  function normalizeReview(review, siteProductId) {
    if (!review || typeof review !== 'object' || review.deleted === true) return null;
    if (review.id == null) return null;
    if (siteProductId && review.product_id != null && String(review.product_id) !== siteProductId) return null;

    const score = Math.round(Number(review.score));
    if (!(score >= 1 && score <= 5)) return null;

    const created = new Date(review.created_at);
    const body = cleanText(review.content, 5000);
    const title = cleanText(review.title, 200);
    if (!body && !title) return null;

    return {
      id: String(review.id),
      score,
      title,
      body,
      name: cleanText(review.user && review.user.display_name, 80) || 'Customer',
      verified: review.verified_buyer === true,
      created: Number.isNaN(created.getTime()) ? null : created.toISOString(),
    };
  }

  function normalizeBottomline(bottomline, reviews, complete) {
    const average = Number(bottomline && bottomline.average_score);
    const total = parseInt(bottomline && bottomline.total_review, 10);
    if (average >= 1 && average <= 5 && total > 0) return { average, total };

    // Only fall back to our own maths when every review was retrieved.
    if (complete && reviews.length) {
      const sum = reviews.reduce((acc, review) => acc + review.score, 0);
      return { average: sum / reviews.length, total: reviews.length };
    }
    return null;
  }

  async function requestReviews(appKey, maxReviews) {
    const first = await fetchPage(appKey, 1);
    const siteProductId = getSiteProductId(first);
    const total = parseInt(first.pagination && first.pagination.total, 10);
    const target = Math.min(Number.isFinite(total) ? total : first.reviews.length, maxReviews);
    const pages = Math.min(Math.ceil(target / PER_PAGE), MAX_PAGES);

    const byId = new Map();
    const collect = (list) => {
      list.forEach((item) => {
        const review = normalizeReview(item, siteProductId);
        if (review && !byId.has(review.id)) byId.set(review.id, review);
      });
    };
    collect(first.reviews);

    for (let page = 2; page <= pages; page += 1) {
      try {
        const next = await fetchPage(appKey, page);
        if (!next.reviews.length) break;
        collect(next.reviews);
      } catch (error) {
        break; // Keep what we already have; never retry in a loop.
      }
    }

    const reviews = Array.from(byId.values())
      .sort((a, b) => (b.created || '').localeCompare(a.created || '') || Number(b.id) - Number(a.id))
      .slice(0, maxReviews);

    const complete = Number.isFinite(total) && byId.size >= total;
    return { summary: normalizeBottomline(first.bottomline, reviews, complete), reviews };
  }

  function loadReviews(appKey, maxReviews) {
    if (!APP_KEY_PATTERN.test(appKey)) {
      return Promise.reject(new ReviewsError('config', 'Add a valid public Yotpo app key in the section settings.'));
    }

    const query = `per_page=${PER_PAGE}&max=${maxReviews}`;
    const cacheKey = `${CACHE_PREFIX}${appKey}:${query}`;
    const cooldownKey = `${CACHE_PREFIX}${appKey}:cooldown`;

    const cached = readFresh(cacheKey);
    if (cached && cached.data) return Promise.resolve(cached.data);
    if (readFresh(cooldownKey)) {
      return Promise.reject(new ReviewsError('rate_limited', 'Yotpo rate limit reached recently; waiting before retrying.'));
    }
    if (inflight.has(cacheKey)) return inflight.get(cacheKey);

    const promise = requestReviews(appKey, maxReviews)
      .then((data) => {
        storage.set(cacheKey, { expires: Date.now() + CACHE_TTL_MS, data });
        return data;
      })
      .catch((error) => {
        if (error.code === 'rate_limited') storage.set(cooldownKey, { expires: Date.now() + COOLDOWN_MS });
        inflight.delete(cacheKey); // allow a later page view / section reload to try again
        throw error;
      });

    inflight.set(cacheKey, promise);
    return promise;
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  }

  function formatScore(value) {
    return (Math.round(value * 10) / 10).toFixed(1);
  }

  class YotpoStoreReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.status = this.querySelector('[data-status]');
      this.dots = this.querySelector('[data-dots]');
      this.autoplayButton = this.querySelector('[data-autoplay-toggle]');
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.min(Math.max(parseInt(this.dataset.speed, 10) || 6, 3), 10) * 1000;
      // Respect reduced motion: start paused, the visitor can still press play.
      if (this.userPaused === undefined) {
        this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
      if (!this.track) return;

      // Every listener is tied to this controller so section reloads never stack listeners.
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.prevButton.addEventListener('click', () => this.goToPage(this.getLayout().page - 1), { signal });
      this.nextButton.addEventListener('click', () => this.goToPage(this.getLayout().page + 1), { signal });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      this.track.addEventListener('click', (event) => this.onTrackClick(event), { signal });
      window.addEventListener('resize', () => this.scheduleUpdate(true), { passive: true, signal });

      if (this.dots) {
        this.dots.addEventListener(
          'click',
          (event) => {
            const dot = event.target.closest('[data-page]');
            if (dot) this.goToPage(parseInt(dot.dataset.page, 10));
          },
          { signal }
        );
      }

      if (this.autoplayButton) {
        this.autoplayButton.addEventListener(
          'click',
          () => {
            this.userPaused = !this.userPaused;
            this.userPaused ? this.stopAutoplay() : this.startAutoplay();
            this.updateAutoplayButton();
          },
          { signal }
        );
      }

      // Pause while the visitor is pointing at or focused inside the slider.
      const pause = () => {
        this.interacting = true;
        this.stopAutoplay();
      };
      const resume = (event) => {
        if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
        if (event.type === 'focusout' && this.matches(':hover')) return;
        this.interacting = false;
        this.startAutoplay();
      };
      this.addEventListener('mouseenter', pause, { signal });
      this.addEventListener('focusin', pause, { signal });
      this.addEventListener('mouseleave', resume, { signal });
      this.addEventListener('focusout', resume, { signal });
      // A swipe means the visitor has taken control: stop rotating until they press play.
      this.track.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType !== 'touch' || !this.timer) return;
          this.userPaused = true;
          this.stopAutoplay();
          this.updateAutoplayButton();
        },
        { passive: true, signal }
      );

      if (this.loaded) {
        this.autoplayPending = true;
        this.scheduleUpdate(true);
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '400px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.observer) this.observer.disconnect();
      window.cancelAnimationFrame(this.frame);
      this.stopAutoplay();
      this.interacting = false;
    }

    load() {
      const appKey = (this.dataset.appKey || '').trim();
      const maxReviews = Math.min(Math.max(parseInt(this.dataset.maxReviews, 10) || 50, 1), PER_PAGE * MAX_PAGES);

      loadReviews(appKey, maxReviews)
        .then((data) => {
          if (!this.isConnected) return;
          if (!data || !Array.isArray(data.reviews) || data.reviews.length === 0) {
            this.fail(new ReviewsError('empty', 'Yotpo returned no published store reviews.'));
            return;
          }
          this.render(data);
        })
        .catch((error) => {
          if (this.isConnected) this.fail(error);
        });
    }

    render({ summary, reviews }) {
      this.loaded = true;
      const sectionId = this.dataset.sectionId || 'section';

      if (summary) {
        const average = formatScore(summary.average);
        const plural = summary.total === 1 ? 'review' : 'reviews';
        this.querySelector('[data-average]').textContent = average;
        this.querySelector('[data-summary-fill]').style.width = `${(summary.average / 5) * 100}%`;
        const stars = this.querySelector('[data-summary-stars]');
        stars.removeAttribute('aria-hidden');
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `Rated ${average} out of 5`);
        this.querySelector('[data-count]').textContent = `Based on ${summary.total} store ${plural}`;
        this.querySelector('[data-summary]').hidden = false;
      }

      const slides = reviews.map((review, index) => this.renderSlide(review, index, reviews.length, sectionId));
      this.track.replaceChildren(...slides);
      this.track.removeAttribute('aria-busy');
      this.track.setAttribute('aria-label', `Store reviews, ${reviews.length} in total. Scroll horizontally to browse.`);
      this.classList.remove('is-loading');
      this.querySelector('[data-controls]').hidden = false;
      if (this.dots) this.dots.hidden = false;
      if (this.autoplayButton) this.autoplayButton.hidden = !this.autoplayEnabled;
      this.updateAutoplayButton();
      this.hidden = false;

      this.autoplayPending = true;
      this.scheduleUpdate(true);
    }

    renderSlide(review, index, count, sectionId) {
      const slide = createElement('li', 'yotpo-store-reviews__slide');
      slide.setAttribute('aria-label', `Review ${index + 1} of ${count}`);

      const card = createElement('article', 'yotpo-store-reviews__card');

      const rating = createElement('p', 'yotpo-store-reviews__rating');
      const stars = createElement('span', 'yotpo-store-reviews__stars');
      stars.setAttribute('aria-hidden', 'true');
      stars.append(createElement('span', 'yotpo-store-reviews__stars-on', '★'.repeat(review.score)));
      stars.append(createElement('span', 'yotpo-store-reviews__stars-off', '★'.repeat(5 - review.score)));
      rating.append(stars, createElement('span', 'visually-hidden', `Rated ${review.score} out of 5`));
      card.append(rating);

      const showTitle = this.dataset.showTitles === 'true';
      if (showTitle && review.title) card.append(createElement('h3', 'yotpo-store-reviews__title h4', review.title));
      // Without titles, a title-only review still shows its title as the text.
      const bodyText = review.body || (showTitle ? '' : review.title);

      if (bodyText) {
        const bodyId = `YotpoStoreReview-${sectionId}-${index}`;
        const body = createElement('p', 'yotpo-store-reviews__body is-clamped', bodyText);
        body.id = bodyId;
        const toggle = createElement('button', 'yotpo-store-reviews__toggle link', 'Read more');
        toggle.type = 'button';
        toggle.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', bodyId);
        toggle.dataset.toggle = '';
        card.append(body, toggle);
      }

      const meta = createElement('footer', 'yotpo-store-reviews__meta');
      meta.append(createElement('span', 'yotpo-store-reviews__author', review.name));
      if (review.verified) meta.append(createElement('span', 'yotpo-store-reviews__verified', 'Verified buyer'));
      if (review.created) {
        const time = createElement('time', 'yotpo-store-reviews__date', dateFormatter.format(new Date(review.created)));
        time.dateTime = review.created.slice(0, 10);
        meta.append(time);
      }
      card.append(meta);

      slide.append(card);
      return slide;
    }

    onTrackClick(event) {
      const toggle = event.target.closest('[data-toggle]');
      if (!toggle || !this.track.contains(toggle)) return;
      const body = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
      if (!body) return;
      const expand = toggle.getAttribute('aria-expanded') !== 'true';
      body.classList.toggle('is-clamped', !expand);
      toggle.setAttribute('aria-expanded', String(expand));
      toggle.textContent = expand ? 'Show less' : 'Read more';
    }

    // Show "Read more" only where the clamped text actually overflows.
    measureClamps() {
      this.track.querySelectorAll('[data-toggle]').forEach((toggle) => {
        if (toggle.getAttribute('aria-expanded') === 'true') return;
        const body = toggle.previousElementSibling;
        toggle.hidden = !(body && body.scrollHeight > body.clientHeight + 1);
      });
    }

    getStep() {
      const slide = this.track.querySelector('.yotpo-store-reviews__slide');
      if (!slide) return this.track.clientWidth || 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap || 1;
    }

    // A "page" is one screenful of cards: 3 on desktop, 2 on tablet, 1 on mobile.
    getLayout() {
      const total = this.track.querySelectorAll('.yotpo-store-reviews__slide').length;
      const step = this.getStep();
      const visible = Math.max(1, Math.min(total || 1, Math.round((this.track.clientWidth + 1) / step)));
      const pages = Math.max(1, Math.ceil(total / visible));
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const scrollLeft = this.track.scrollLeft;
      const page =
        maxScroll > 1 && scrollLeft >= maxScroll - 1
          ? pages - 1
          : Math.min(pages - 1, Math.max(0, Math.round(scrollLeft / (visible * step))));
      return { total, step, visible, pages, page, maxScroll, scrollLeft };
    }

    // Loops: past the last page goes back to the first, and vice versa.
    goToPage(index) {
      const { pages, visible, step, maxScroll } = this.getLayout();
      const target = ((index % pages) + pages) % pages;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: Math.min(target * visible * step, maxScroll), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    scheduleUpdate(remeasure) {
      if (remeasure) this.needsMeasure = true;
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => {
        if (this.needsMeasure) {
          this.needsMeasure = false;
          this.measureClamps();
        }
        this.updateControls();
        if (this.autoplayPending) {
          this.autoplayPending = false;
          this.startAutoplay();
        }
      });
    }

    updateControls() {
      if (!this.loaded) return;
      const { total, visible, pages, page, step, maxScroll, scrollLeft } = this.getLayout();
      if (!total) return;

      this.isStatic = maxScroll <= 1;
      this.prevButton.disabled = this.isStatic;
      this.nextButton.disabled = this.isStatic;
      this.querySelector('[data-controls]').classList.toggle('is-static', this.isStatic);
      if (this.isStatic) this.stopAutoplay();

      if (this.dots) {
        this.dots.hidden = this.isStatic;
        if (this.dots.childElementCount !== pages) this.renderDots(pages);
        Array.from(this.dots.children).forEach((dot, index) => {
          const active = index === page;
          dot.classList.toggle('is-active', active);
          active ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current');
        });
      }

      const first = Math.min(total - visible, Math.max(0, Math.round(scrollLeft / step)));
      const text = visible === 1 ? `Review ${first + 1} of ${total}` : `Reviews ${first + 1}–${first + visible} of ${total}`;
      if (this.status.textContent !== text) this.status.textContent = text;
    }

    renderDots(pages) {
      const dots = [];
      for (let index = 0; index < pages; index += 1) {
        const dot = createElement('button', 'yotpo-store-reviews__dot');
        dot.type = 'button';
        dot.dataset.page = String(index);
        dot.setAttribute('aria-label', `Show reviews, page ${index + 1} of ${pages}`);
        dots.push(dot);
      }
      this.dots.replaceChildren(...dots);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || this.userPaused || this.interacting || !this.loaded || this.isStatic) return;
      // Don't announce every automatic change to screen readers.
      this.status.setAttribute('aria-live', 'off');
      this.timer = window.setInterval(() => this.goToPage(this.getLayout().page + 1), this.autoplayDelay);
    }

    stopAutoplay() {
      window.clearInterval(this.timer);
      this.timer = null;
      if (this.status) this.status.setAttribute('aria-live', 'polite');
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      const paused = this.userPaused;
      this.autoplayButton.setAttribute('aria-label', paused ? 'Start automatic scrolling' : 'Pause automatic scrolling');
      this.autoplayButton.classList.toggle('is-paused', paused);
    }

    fail(error) {
      this.loaded = false;
      this.stopAutoplay();
      this.classList.remove('is-loading');
      const diagnostics = this.querySelector('[data-diagnostics]');

      if (this.designMode && diagnostics) {
        // Theme editor only: explain what went wrong, never show placeholder reviews.
        diagnostics.textContent = `Yotpo store reviews: ${error.message || 'Unknown error.'} (${error.code || 'error'}). This message is only visible in the theme editor; the section is hidden on the storefront.`;
        diagnostics.hidden = false;
        this.track.replaceChildren();
        this.track.removeAttribute('aria-busy');
        this.querySelector('[data-controls]').hidden = true;
        return;
      }

      // Storefront: stay quiet and remove the empty section rather than show broken controls.
      this.hidden = true;
    }
  }

  customElements.define('yotpo-store-reviews', YotpoStoreReviews);
})();
